import { useQuery } from "@tanstack/react-query";
import { get } from "./api";
import type { Deployment } from "./types";

/**
 * Where this deployment lives.
 *
 * ## Why the panel asks instead of knowing
 *
 * This bundle is built once and served by every environment (`api/staticUi.ts`), so
 * a hostname compiled into it with a `VITE_` variable would be decided by whoever
 * ran the build rather than by the host that is serving it — and the failure is
 * silent: a production panel printing a staging WebSocket URL looks exactly like a
 * correct one until an installer configures a charger with it. The server knows its
 * own public address because it was told; this asks.
 *
 * ## Cached for the session
 *
 * The answer is a pair of hostnames read from the serving process's environment. It
 * cannot change without a restart, and a restart ends this page's session anyway —
 * so it is fetched once and never revalidated, rather than riding the 10-second
 * default that exists for live hardware state.
 */

export const DEPLOYMENT_KEY = ["deployment"] as const;

export interface DeploymentState {
  /** `wss://host/ocpp`, or `undefined` while loading and when unconfigured. */
  readonly ocppBaseUrl: string | undefined;
  /** The pay-ui origin, or `undefined` while loading and with no payment rail. */
  readonly payUiBaseUrl: string | undefined;
  /**
   * The request has not resolved. Told apart from "configured as nothing" because a
   * panel that says *not configured* while it is still asking sends an operator to
   * edit an environment file they did not need to touch.
   */
  readonly isResolving: boolean;
  /**
   * The request failed — a 403 for a principal without `read`, or the endpoint not
   * existing on an older server. Distinguished from an unconfigured deployment for
   * the same reason: "set this variable" is the wrong instruction when the variable
   * may already be set and the panel simply could not ask.
   */
  readonly hasFailed: boolean;
}

export function useDeployment(): DeploymentState {
  const query = useQuery({
    queryKey: DEPLOYMENT_KEY,
    queryFn: () => get<Deployment>("/deployment"),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  return {
    // `?? undefined` collapses the wire's `null` — "this deployment has not
    // declared an address" — into the same absence as "not asked yet", because
    // every consumer treats them identically: no string, no section.
    ocppBaseUrl: query.data?.ocppBaseUrl ?? undefined,
    payUiBaseUrl: query.data?.payUiBaseUrl ?? undefined,
    isResolving: query.isPending,
    hasFailed: query.isError,
  };
}
